<script setup lang="ts">
  import type {
    CorrectedWord,
    FillLogsPaperTarget,
    FillLogsReading,
    FillLogsTableRow,
  } from '../model/fill-logs-text';
  import FillLogsBeforeLine from './FillLogsBeforeLine.vue';
  import FillLogsValueText from './FillLogsValueText.vue';
  import { READING_BUTTON, TONE_BAR, TONE_TINT, toneSaid } from './fill-logs-tone';

  defineProps<{ columns: string[]; rows: FillLogsTableRow[]; correctedWord: CorrectedWord }>();
  const emit = defineEmits<{ (e: 'show', target: FillLogsPaperTarget): void }>();

  const CELL = 'border-b border-gray-02 px-2.5 py-1.5 align-top';
  const TD = 'border-b border-gray-02 p-0 align-top';

  // A new row is tinted as a whole, so only a cell changed in an older row is tinted on its own.
  function cellTint(row: FillLogsTableRow, cell: FillLogsReading | null): string {
    return !row.isNew && cell?.tone ? TONE_TINT[cell.tone] : '';
  }
</script>

<template>
  <div class="overflow-x-auto">
    <table class="w-full border-collapse text-left text-sm/5">
      <thead>
        <tr>
          <th
            scope="col"
            :class="[CELL, 'w-14 border-l-[3px] border-l-transparent text-xs/4 font-semibold text-gray-03']"
          >
            Linha
          </th>
          <th
            v-for="(column, index) in columns"
            :key="index"
            scope="col"
            :class="[CELL, 'text-xs/4 font-semibold text-gray-03']"
          >
            {{ column }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in rows"
          :key="row.key"
          :class="row.isNew ? TONE_TINT.filled : ''"
          :data-new="row.isNew || undefined"
          data-testid="fill-logs-all-row"
        >
          <th
            scope="row"
            :class="[CELL, 'border-l-[3px] text-xs/5 font-semibold text-gray-03 tabular-nums', row.isNew ? TONE_BAR.filled : 'border-l-transparent']"
          >
            {{ row.number }}<span
              v-if="row.isNew"
              class="sr-only"
            >, linha nova nesta resposta</span>
          </th>
          <td
            v-for="(cell, index) in row.cells"
            :key="index"
            :class="[TD, 'min-w-24 text-gray-07 [overflow-wrap:anywhere]', cellTint(row, cell)]"
            :data-tone="cell?.tone ?? undefined"
            data-testid="fill-logs-all-cell"
          >
            <button
              v-if="cell"
              type="button"
              class="block h-full px-2.5 py-1.5"
              :class="READING_BUTTON"
              :data-key="cell.key"
              @click="emit('show', { targets: [cell.key], tone: cell.tone, label: `${columns[index]} · Linha ${row.number}` })"
            >
              <FillLogsValueText :value="cell.value" /><span
                v-if="cell.tone && !row.isNew"
                class="sr-only"
              >, {{ toneSaid(cell.tone, correctedWord) }}</span>
              <FillLogsBeforeLine
                v-if="cell.before"
                :before="cell.before"
              />
              <span class="sr-only">. Ver no registro.</span>
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
